import { useState } from 'react';
import { toast } from 'react-toastify';

import { TextField } from '~/components/form';
import { Alert, Button, Card, CardBody, CardHeader, CardSection } from '~/components/ui';
import { errorText } from '~/lib/apiError';
import {
  CHANNEL_LABELS,
  filledChannels,
  parseSampleData,
  TEST_SEND_CHANNELS,
  type TemplateFormValues,
  toDesignTestRequest,
  toDraftTestRequest,
  toTemplateMessage,
} from '~/lib/notification/templateForm';
import { useSendMessageMutation } from '~/modules/query/notification/messages';
import { usePreviewTemplateMutation } from '~/modules/query/notification/templates';
import type { NotificationChannel } from '~/modules/query/notification/types';

interface TemplateTestPanelProps {
  form: TemplateFormValues;
  /** The tab being edited: its sample data fills the placeholders. */
  channel: NotificationChannel;
  /** The saved template; a linked design is test-sent by it, so there is none to send on create. */
  templateId?: string;
  dirty: boolean;
  /** An old version is on screen; a test by templateId would not send it. */
  readOnly: boolean;
}

/**
 * The right column of the template form: renders the draft on the service, through the engine the
 * Worker sends with, and sends the draft to a real address. Both use the form as it stands, so an
 * unsaved or unpublished draft can be tried before it goes live. A linked email design is the
 * exception: it has no render here and is test-sent from the saved template, by its templateId.
 */
export default function TemplateTestPanel({
  form,
  channel,
  templateId,
  dirty,
  readOnly,
}: TemplateTestPanelProps) {
  const [preview, { data, error, isLoading }] = usePreviewTemplateMutation();
  const [sendMessage, { isLoading: sending }] = useSendMessageMutation();
  const [recipients, setRecipients] = useState<Partial<Record<NotificationChannel, string>>>({});
  const [sendError, setSendError] = useState<string | null>(null);

  const sample = parseSampleData(form[channel].sampleData);
  // A linked design renders on the service from the saved template, not from the form.
  const usesDesign = channel === 'email' && form.email.designTemplateId !== '';
  const hasBody = usesDesign || form[channel].body.trim() !== '';
  const blocked = filledChannels(form).length === 0 || !sample.ok;
  const rendered = data?.[channel];
  const recipient = recipients[channel] ?? '';
  const isEmail = channel === 'email';
  const designBlocked = !templateId
    ? 'Create the template to send a test of its design.'
    : readOnly
      ? 'Switch to the latest version to send a test of its design.'
      : dirty
        ? 'Save your changes to send a test of the design.'
        : undefined;
  const sendBlocked = !hasBody
    ? `Write the ${CHANNEL_LABELS[channel]} message to send it.`
    : usesDesign && designBlocked
      ? designBlocked
      : !sample.ok
        ? 'Fix the sample data to send a test.'
        : !recipient.trim()
          ? `Enter ${isEmail ? 'an email address' : 'a mobile number'} to send to.`
          : undefined;

  const render = () => {
    if (!sample.ok) return;
    void preview({ message: toTemplateMessage(form), data: sample.value });
  };

  const send = async () => {
    setSendError(null);
    try {
      const request =
        usesDesign && templateId
          ? toDesignTestRequest(templateId, form, recipient)
          : toDraftTestRequest(form, channel, recipient);
      await sendMessage(request).unwrap();
      toast.success(`Test queued to ${recipient.trim()}`);
    } catch (failure) {
      setSendError(errorText(failure, 'The test could not be sent. Try again.'));
    }
  };

  return (
    <Card>
      <CardHeader
        title="Preview and test"
        description={`Try the ${CHANNEL_LABELS[channel]} message with its sample data. Unsaved edits are included.`}
      />
      <CardBody className="space-y-6">
        <CardSection
          title="Preview"
          actions={
            !usesDesign && (
              <Button
                variant="outline"
                size="sm"
                onClick={render}
                loading={isLoading}
                disabled={blocked}
                title={
                  blocked ? 'Write a message and fix its sample data to preview it.' : undefined
                }
                testId="template-preview-button"
              >
                Render preview
              </Button>
            )
          }
        >
          {error && (
            <Alert
              variant="error"
              message={errorText(error, 'The preview could not be rendered. Try again.')}
            />
          )}
          {usesDesign && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This email is a linked design; its raster is in the Email tab. Send a test to see it
              rendered with the sample data.
            </p>
          )}
          {!usesDesign && !rendered && !error && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select Render preview to see this channel.
            </p>
          )}
          {!usesDesign && rendered && (
            <div className="space-y-3" data-testid="template-preview-result">
              {'subject' in rendered && (
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {rendered.subject}
                </p>
              )}
              {isEmail ? (
                <iframe
                  title="Email preview"
                  sandbox=""
                  srcDoc={rendered.body}
                  className="h-96 w-full rounded-lg border border-gray-200 bg-white dark:border-gray-800"
                />
              ) : (
                <p className="whitespace-pre-wrap rounded-lg border border-gray-200 p-4 text-sm text-gray-800 dark:border-gray-800 dark:text-white/90">
                  {rendered.body}
                </p>
              )}
            </div>
          )}
        </CardSection>

        <CardSection title="Send a test">
          {TEST_SEND_CHANNELS.includes(channel) ? (
            <div className="space-y-4">
              {sendError && <Alert variant="error" message={sendError} />}
              <TextField
                id={`template-test-${channel}`}
                testId={`template-test-${channel}`}
                label={isEmail ? 'Send to email' : 'Send to mobile'}
                type={isEmail ? 'email' : 'text'}
                value={recipient}
                onChange={(value) => setRecipients((current) => ({ ...current, [channel]: value }))}
                helperText={
                  usesDesign
                    ? "Goes through the default provider: the saved template's design, filled with the sample data."
                    : "Goes through the default provider, with this draft's message and sample data. The live version is not used."
                }
              />
              <Button
                variant="outline"
                onClick={send}
                loading={sending}
                disabled={!!sendBlocked}
                title={sendBlocked}
                testId="template-test-send"
              >
                Send test
              </Button>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Browser messages cannot be sent as a test. Preview them instead.
            </p>
          )}
        </CardSection>
      </CardBody>
    </Card>
  );
}
